import { useDialog } from 'muibox';
import { useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';

const useSextforceBigBrotherRestrictedWords = () => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const dialog = useDialog();
    const params = useParams();
    const axios = useAxios();

    const [sextforceBigBrotherRestrictedWordsWhiteListLoading, setSextforceBigBrotherRestrictedWordsWhiteListLoading] = useState(false);
    const [sextforceBigBrotherRestrictedWordsResetCountersLoading, setSextforceBigBrotherRestrictedWordsResetCountersLoading] =
        useState(false);
    const [
        sextforceBigBrotherRestrictedWordsCustomRestrictedWordsLoading,
        setSextforceBigBrotherRestrictedWordsCustomRestrictedWordsLoading,
    ] = useState(false);
    const [sextforceBigBrotherAllowedUsernamesLoading, setSextforceBigBrotherAllowedUsernamesLoading] = useState(false);
    const [sextforceBigBrotherBlockAllUsernamesLoading, setSextforceBigBrotherBlockAllUsernamesLoading] = useState(false);

    // Fetch the restricted words
    const fetchRestrictedWords = async (): Promise<Set<string> | void> => {
        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.sextforce.base}${params.userId}/bigBrother/restrictedWords`;

            return axios
                .get(query)
                .then(async response => new Set(response.data as string[]))
                .catch(error => {
                    console.error(error);
                    handleHttpError(error, dialog);
                });
        }
    };

    const restrictedWordsMaster = useQuery(
        ['restrictedWordsMaster', params.userId],
        () => {
            return fetchRestrictedWords();
        },
        {
            refetchOnWindowFocus: false,
            // Stake time 1 hour
            staleTime: 60 * 60 * 1000,
            enabled: userContext.jwtToken && settingsContext.apiKey && userContext.user.username && params.userId ? true : false,
        },
    );

    /**
     * Sets the white list of restricted words
     * @param whiteList Array of strings
     * @returns
     */
    const setSextforceBigBrotherRestrictedWordsWhiteList = async (whiteList: string[]): Promise<{ whiteList: string[] }> => {
        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.sextforce.base}${params.userId}/bigBrother/restrictedWords/whiteList`;

            setSextforceBigBrotherRestrictedWordsWhiteListLoading(true);

            return axios
                .put(query, {
                    whiteList,
                })
                .then(async response => {
                    setSextforceBigBrotherRestrictedWordsWhiteListLoading(false);

                    return response.data;
                })
                .catch(error => {
                    console.error(error);
                    setSextforceBigBrotherRestrictedWordsWhiteListLoading(false);
                    handleHttpError(error, dialog);

                    return Promise.reject(error);
                });
        }

        return { whiteList: [] };
    };

    /**
     * Sets the custom restricted words list
     * @param customRestrictedWords Array of strings
     * @returns
     */
    const setSextforceBigBrotherCustomRestrictedWordsList = async (
        customRestrictedWords: string[],
    ): Promise<{ customRestrictedWords: string[] }> => {
        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.sextforce.base}${params.userId}/bigBrother/restrictedWords/customRestrictedWords`;

            setSextforceBigBrotherRestrictedWordsCustomRestrictedWordsLoading(true);

            return axios
                .put(query, {
                    customRestrictedWords,
                })
                .then(async response => {
                    setSextforceBigBrotherRestrictedWordsCustomRestrictedWordsLoading(false);

                    return response.data;
                })
                .catch(error => {
                    console.error(error);
                    setSextforceBigBrotherRestrictedWordsCustomRestrictedWordsLoading(false);
                    handleHttpError(error, dialog);

                    return Promise.reject(error);
                });
        }

        return { customRestrictedWords: [] };
    };

    /**
     * Sets blockAllUsersnames option
     * @param blockAllUsersnames Boolean
     * @returns
     */
    const setSextforceBigBrotherBlockAllUsernames = async (blockAllUsernames: boolean): Promise<{ blockAllUsernames: boolean }> => {
        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.sextforce.base}${params.userId}/bigBrother/restrictedWords/blockAllUsernames`;

            setSextforceBigBrotherBlockAllUsernamesLoading(true);

            return axios
                .put(query, {
                    blockAllUsernames,
                })
                .then(async response => {
                    setSextforceBigBrotherBlockAllUsernamesLoading(false);

                    return response.data;
                })
                .catch(error => {
                    console.error(error);
                    setSextforceBigBrotherBlockAllUsernamesLoading(false);
                    handleHttpError(error, dialog);

                    return Promise.reject(error);
                });
        }

        return { blockAllUsernames: false };
    };

    /**
     * Sets the allowed usernames list
     * @param allowedUsernames Array of strings
     * @returns
     */
    const setSextforceBigBrotherAllowedUsernames = async (allowedUsernames: string[]): Promise<{ allowedUsernames: string[] }> => {
        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.sextforce.base}${params.userId}/bigBrother/restrictedWords/allowedUsernames`;

            setSextforceBigBrotherAllowedUsernamesLoading(true);

            return axios
                .put(query, {
                    allowedUsernames,
                })
                .then(async response => {
                    setSextforceBigBrotherAllowedUsernamesLoading(false);

                    return response.data;
                })
                .catch(error => {
                    console.error(error);
                    setSextforceBigBrotherAllowedUsernamesLoading(false);
                    handleHttpError(error, dialog);

                    return Promise.reject(error);
                });
        }

        return { allowedUsernames: [] };
    };

    const setSextforceBigBrotherRestrictedWordsResetCounters = async (): Promise<any> => {
        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.sextforce.base}${params.userId}/bigBrother/restrictedWords/counters`;

            setSextforceBigBrotherRestrictedWordsResetCountersLoading(true);

            return axios
                .delete(query)
                .then(async response => {
                    setSextforceBigBrotherRestrictedWordsResetCountersLoading(false);

                    return response.data;
                })
                .catch(error => {
                    console.error(error);
                    setSextforceBigBrotherRestrictedWordsResetCountersLoading(false);
                    handleHttpError(error, dialog);
                });
        }
    };

    return {
        ...restrictedWordsMaster,
        setSextforceBigBrotherRestrictedWordsWhiteList,
        sextforceBigBrotherRestrictedWordsWhiteListLoading,
        setSextforceBigBrotherRestrictedWordsResetCounters,
        SextforceBigBrotherRestrictedWordsResetCountersLoading: sextforceBigBrotherRestrictedWordsResetCountersLoading,
        setSextforceBigBrotherCustomRestrictedWordsList,
        sextforceBigBrotherRestrictedWordsCustomRestrictedWordsLoading,
        setSextforceBigBrotherAllowedUsernames,
        sextforceBigBrotherAllowedUsernamesLoading,
        setSextforceBigBrotherBlockAllUsernames,
        sextforceBigBrotherBlockAllUsernamesLoading,
    };
};

export default useSextforceBigBrotherRestrictedWords;
